import {
    Box,
    Button,
    Card,
    CardContent,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { registerUser } from '../../api/authApi';
import { useAppDialog } from '../../Hooks/useAppDialog';
import { IRegisterUser } from '../../interface/User';
import { roleHierarchy } from '../../Utils/getAccess';
import AppDialogMessage from '../Common/AppDialogMessage';
import { Category } from '../NavBar/MenuItems';

interface RegistrationFormProps {
    email?: string;
    location?: string;
    scope?: 'User' | 'Admin' | 'SuperAdmin';
    category?: Category;
    company?: string;
    first_name?: string;
    last_name?: string;
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({
    email,
    location,
    first_name,
    last_name,
    scope,
}) => {
    const user = useSelector((state: any) => state.user.user);

    const { dialog, openDialog, closeDialog } = useAppDialog();

    /**
     * Initial form state.
     * Defaults depend on the currently logged-in user's category.
     */
    const initialState: IRegisterUser = {
        email: '',
        password: '',
        confirmPassword: '',
        location: 'DC-Caledonia',
        scope: user?.category === 'CLIENT' ? 'Admin' : 'User',
        first_name: '',
        last_name: '',
        category: user?.category || 'CLIENT',
        company: '',
    };

    const [form, setForm] = useState<IRegisterUser>(initialState);

    /**
     * Scope options are derived from role hierarchy.
     */
    const scopeOptions = useMemo(() => Array.from(Object.keys(roleHierarchy)), []);

    /**
     * Pre-fill support when this form is used for editing / preloaded values.
     */
    useEffect(() => {
        setForm((prev) => ({
            ...prev,
            email: email ?? prev.email,
            location: location ?? prev.location,
            first_name: first_name ?? prev.first_name,
            last_name: last_name ?? prev.last_name,
            scope: scope ?? prev.scope,
        }));
    }, [email, location, first_name, last_name, scope]);

    /**
     * Generic form field updater.
     */
    const updateForm = <K extends keyof IRegisterUser>(key: K, value: IRegisterUser[K]) => {
        setForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    /**
     * Validate registration form before submission.
     */
    const validateForm = () => {
        if (
            !form.email ||
            !form.first_name ||
            !form.last_name ||
            !form.password ||
            !form.confirmPassword ||
            !form.location ||
            !form.scope
        ) {
            return { status: false, message: 'Fill in all the fields.' };
        }

        if (form.password !== form.confirmPassword) {
            return { status: false, message: 'Passwords do not match.' };
        }

        if (!form.email.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)) {
            return { status: false, message: 'Invalid email.' };
        }

        return { status: true, message: '' };
    };

    /**
     * Submit registration form.
     * Uses the shared dialog system instead of alert().
     */
    const handleSubmit = async () => {
        const valid = validateForm();

        if (!valid.status) {
            openDialog({
                variant: 'error',
                title: 'Validation Error',
                message: valid.message,
                confirmText: 'Close',
                hideCancel: true,
            });
            return;
        }

        try {
            await registerUser(form);

            setForm(initialState);

            openDialog({
                variant: 'success',
                title: 'User Registered',
                message: 'User registered successfully.',
                confirmText: 'Okay',
                hideCancel: true,
            });
        } catch (error: any) {
            openDialog({
                variant: 'error',
                title: 'Registration Failed',
                message: error?.message || `Error registering user: ${form.first_name}`,
                confirmText: 'Close',
                hideCancel: true,
            });
        }
    };

    return (
        <>
            <Box
                sx={{
                    p: { xs: 2, md: 3 },
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <Card
                    elevation={0}
                    sx={{
                        width: '100%',
                        maxWidth: 1100,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            px: { xs: 2, md: 3 },
                            py: 2,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            background:
                                'linear-gradient(180deg, rgba(69,100,145,0.08) 0%, rgba(69,100,145,0.02) 100%)',
                        }}
                    >
                        <Typography variant="h5" fontWeight={800}>
                            Register
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Create a new user account with role, category, and company access.
                        </Typography>
                    </Box>

                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                        <Stack spacing={3}>
                            <Grid container spacing={2}>
                                <Grid size={12}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Email"
                                        type="email"
                                        value={form.email}
                                        placeholder="Enter your email..."
                                        onChange={(e) => updateForm('email', e.target.value || '')}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="First Name"
                                        value={form.first_name}
                                        placeholder="Enter your first name..."
                                        onChange={(e) => updateForm('first_name', e.target.value || '')}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Last Name"
                                        value={form.last_name}
                                        placeholder="Enter your last name..."
                                        onChange={(e) => updateForm('last_name', e.target.value || '')}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Location"
                                        value={form.location}
                                        onChange={(e) => updateForm('location', e.target.value || '')}
                                    >
                                        <MenuItem value="DC-Caledonia">DC-Caledonia</MenuItem>
                                        <MenuItem value="DC">DC</MenuItem>
                                    </TextField>
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    {user?.category === 'CLIENT' ? (
                                        <TextField
                                            select
                                            fullWidth
                                            label="Scope"
                                            value={form.scope}
                                            onChange={(e) =>
                                                updateForm(
                                                    'scope',
                                                    e.target.value as 'User' | 'Admin' | 'SuperAdmin'
                                                )
                                            }
                                        >
                                            {scopeOptions.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    ) : (
                                        <TextField
                                            select
                                            fullWidth
                                            label="Scope"
                                            value={form.scope}
                                            onChange={(e) =>
                                                updateForm('scope', e.target.value as 'User' | 'Admin')
                                            }
                                        >
                                            <MenuItem value="User">User</MenuItem>
                                            <MenuItem value="Admin">Admin</MenuItem>
                                        </TextField>
                                    )}
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Category"
                                        value={form.category}
                                        onChange={(e) => updateForm('category', e.target.value as Category)}
                                    >
                                        <MenuItem value="CLIENT">CLIENT</MenuItem>
                                        <MenuItem value="VENDOR">VENDOR</MenuItem>
                                        <MenuItem value="TRAILER">TRAILER</MenuItem>
                                    </TextField>
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Company"
                                        value={form.company}
                                        onChange={(e) => updateForm('company', e.target.value || '')}
                                    >
                                        <MenuItem value="G2000">G2000</MenuItem>
                                        <MenuItem value="SVP-SPORTS">SVP-SPORTS</MenuItem>
                                        <MenuItem value="SMARTWAY">SMARTWAY</MenuItem>
                                        <MenuItem value="OTHER">OTHER</MenuItem>
                                    </TextField>
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Password"
                                        type="password"
                                        value={form.password}
                                        placeholder="Enter your password..."
                                        onChange={(e) => updateForm('password', e.target.value || '')}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Confirm Password"
                                        type="password"
                                        value={form.confirmPassword}
                                        placeholder="Enter your password again..."
                                        onChange={(e) =>
                                            updateForm('confirmPassword', e.target.value || '')
                                        }
                                    />
                                </Grid>
                            </Grid>

                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <Button variant="contained" onClick={handleSubmit}>
                                    Register
                                </Button>
                            </Paper>
                        </Stack>
                    </CardContent>
                </Card>
            </Box>

            <AppDialogMessage
                open={dialog.open}
                title={dialog.title}
                message={dialog.message}
                variant={dialog.variant}
                confirmText={dialog.confirmText}
                cancelText={dialog.cancelText}
                hideCancel={dialog.hideCancel}
                onConfirm={dialog.onConfirm}
                onClose={closeDialog}
            />
        </>
    );
};

export default RegistrationForm;